import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type {
  MovementListProps,
  MovementsSummaryProps,
} from '@services/MovimentationService';
import MovimentationService from '@services/MovimentationService';

import { toastifyApiErrors } from '@utils';
import { useFilterParams, useAuth } from '@hooks';
import { loadParamsBasedOnKeys } from 'hooks/services/beneficiaries/list/utils';
import { FilterParamsEnum } from 'constants/private/global/filterParamsEnum';

export function useMovementsState() {
  const { currentPage, searchParam, tableOrder, mappedFilters } =
    useFilterParams();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedCompany, selectedPartnerCompany } = useAuth();
  const [searchParams] = useSearchParams();

  const movementService = useMemo(() => new MovimentationService(), []);

  const companyId = selectedCompany?.id ?? '';
  const partnerCompany = selectedPartnerCompany?.partnerCompany ?? '';

  const isEnabled = Boolean(companyId) && Boolean(partnerCompany);

  const mappedParams = loadParamsBasedOnKeys(searchParams, [
    FilterParamsEnum.STATUS,
    FilterParamsEnum.ORGANIZATIONAL_GROUP,
    FilterParamsEnum.START_DATE,
    FilterParamsEnum.END_DATE,
  ]);

  const movementsState = useQuery<MovementListProps>({
    queryKey: [
      'movementsState',
      mappedFilters,
      searchParam,
      currentPage,
      tableOrder,
      companyId,
      partnerCompany,
      mappedParams,
    ],
    queryFn: () =>
      movementService.fetchMovements({
        filters: {
          ...mappedFilters,
          ...searchParam,
          ...tableOrder,
          ...mappedParams,
          offset: 10 * (currentPage - 1),
          limit: 10,
        },
        companyId,
      }),
    enabled: isEnabled,
    onError: error => {
      toastifyApiErrors(error, enqueueSnackbar);
    },
  });

  const movementsSummaryState = useQuery<MovementsSummaryProps[]>({
    queryKey: [
      'movementsSummaryState',
      mappedFilters,
      searchParam,
      companyId,
      partnerCompany,
      mappedParams,
    ],
    queryFn: () =>
      movementService.fetchMovementsSummary({
        companyId,
        ...mappedFilters,
        ...searchParam,
        ...mappedParams,
      }),
    enabled: isEnabled,
    onError: error => {
      toastifyApiErrors(error, enqueueSnackbar);
    },
  });

  const refreshMovementsStates = () => {
    movementsSummaryState.remove();
    movementsState.remove();

    movementsSummaryState.refetch();
    movementsState.refetch();
  };

  return { movementsSummaryState, movementsState, refreshMovementsStates };
}
